import React, { useState, useRef, useEffect } from 'react';
import { TextField, Grid } from '@mui/material';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import FormContainer from '../../components/FormContainer';
import { addYojanaCategory, updateYojanaCategory } from '../../utils/api';

const YojanaCategoryForm = ({ editCategory = null, onSuccess }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);
  const isEditMode = !!editCategory;
  const onSuccessTimerRef = useRef(null);

  useEffect(
    () => () => {
      if (onSuccessTimerRef.current != null) {
        clearTimeout(onSuccessTimerRef.current);
        onSuccessTimerRef.current = null;
      }
    },
    []
  );

  const getCategoryId = (category) =>
    category?.categoryId ??
    category?.yojanaCategoryId ??
    category?.id;

  const getCategoryName = (category) =>
    category?.categoryName ??
    category?.yojanaCategoryName ??
    category?.name ??
    '';

  const validationSchema = Yup.object({
    categoryName: Yup.string()
      .required('Category name is required')
      .max(100, 'Category name must be at most 100 characters'),
    description: Yup.string()
      .required('Description is required')
      .max(500, 'Description must be at most 500 characters'),
  });

  const formik = useFormik({
    initialValues: {
      categoryName: editCategory ? getCategoryName(editCategory) : '',
      description: editCategory?.description ?? '',
    },
    validationSchema,
    enableReinitialize: true,
    onSubmit: async (values, { resetForm }) => {
      setIsSubmitting(true);
      setSuccessMessage(null);
      setErrorMessage(null);

      try {
        if (isEditMode) {
          const categoryId = getCategoryId(editCategory);
          await updateYojanaCategory(categoryId, values, {
            previousData: editCategory,
            identifier: getCategoryName(editCategory)
          });
          setSuccessMessage('Yojana category updated successfully!');
        } else {
          await addYojanaCategory(values, {
            identifier: values.categoryName
          });
          setSuccessMessage('Yojana category added successfully!');
          resetForm();
        }

        if (onSuccess) {
          if (onSuccessTimerRef.current != null) clearTimeout(onSuccessTimerRef.current);
          onSuccessTimerRef.current = setTimeout(() => {
            onSuccessTimerRef.current = null;
            onSuccess();
          }, 1500);
        }
      } catch (error) {
        console.error(`Error ${isEditMode ? 'updating' : 'adding'} yojana category:`, error);
        setErrorMessage(
          error.response?.data?.message ||
          `Failed to ${isEditMode ? 'update' : 'add'} yojana category. Please try again.`
        );
      } finally {
        setIsSubmitting(false);
      }
    },
  });

  const handleCloseSnackbar = () => {
    setSuccessMessage(null);
    setErrorMessage(null);
  };

  return (
    <FormContainer
      title={isEditMode ? 'Edit Yojana Category' : 'Add Yojana Category'}
      onSubmit={formik.handleSubmit}
      isSubmitting={isSubmitting}
      successMessage={successMessage}
      errorMessage={errorMessage}
      onCloseSnackbar={handleCloseSnackbar}
    >
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <TextField
            fullWidth
            id="categoryName"
            name="categoryName"
            label="Category Name"
            value={formik.values.categoryName}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.categoryName && Boolean(formik.errors.categoryName)}
            helperText={formik.touched.categoryName && formik.errors.categoryName}
            margin="normal"
            variant="outlined"
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            id="description"
            name="description"
            label="Description"
            value={formik.values.description}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.description && Boolean(formik.errors.description)}
            helperText={formik.touched.description && formik.errors.description}
            margin="normal"
            variant="outlined"
            multiline
            rows={4}
          />
        </Grid>
      </Grid>
    </FormContainer>
  );
};

export default YojanaCategoryForm;

